import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Search, CreditCard, Webhook, ChevronRight, Users, Smartphone, KeyRound, AlertTriangle, Loader2, CalendarPlus, BellRing, Mail, MessageCircle, Send, RefreshCw, Calendar, CheckCircle2 } from "lucide-react";
import { setCalendarInviteMode } from "@/lib/calendarPrefs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useRazorpay } from "@/lib/useRazorpay";
import { useConfirm } from "@/components/Confirm";

const SUB_STATUS_META = {
  trial:         { label: "Trial",          cls: "bg-blue-50 text-blue-700 border-blue-200" },
  trial_expired: { label: "Trial Expired",  cls: "bg-red-50 text-red-700 border-red-200" },
  active:        { label: "Active",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expired:       { label: "Expired",        cls: "bg-red-50 text-red-700 border-red-200" },
  suspended:     { label: "Suspended",      cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function Settings() {
  const confirm = useConfirm();
  const { user, refreshUser } = useAuth();
  const [savingIdle, setSavingIdle] = useState(false);
  const savePasswordless = async (on) => {
    setSavingIdle(true);
    try {
      await api.post("/auth/session-settings", { passwordlessLogin: on });
      await refreshUser();
      toast.success(on ? "Login codes switched on" : "Login codes switched off");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingIdle(false);
    }
  };
  const saveIdleLogout = async (minutes) => {
    setSavingIdle(true);
    try {
      await api.post("/auth/session-settings", { idleLogoutMinutes: minutes });
      await refreshUser();
      toast.success(minutes ? "Auto logout updated" : "You'll stay logged in until you log out");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingIdle(false);
    }
  };
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const { openCheckout } = useRazorpay();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlans, setUpgradePlans] = useState([]);
  const [upgradePlansLoading, setUpgradePlansLoading] = useState(false);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", chat_id: "", chat_type: "individual", phone: "" });
  const [addForm, setAddForm] = useState({ name: "", chat_id: "", chat_type: "individual", phone: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Date format settings
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [savingDateFormat, setSavingDateFormat] = useState(false);

  // Skip processing settings
  const [skipGroups, setSkipGroups] = useState(false);
  const [skipIndividuals, setSkipIndividuals] = useState(false);
  const [savingSkipSettings, setSavingSkipSettings] = useState(false);

  // Chat skip list
  const [skippedChats, setSkippedChats] = useState([]);
  // "all" = process everything except the skip list; "selected" = only allowedChats
  const [chatFilterMode, setChatFilterMode] = useState("all");
  const [allowedChats, setAllowedChats] = useState([]);
  const [savingFilterMode, setSavingFilterMode] = useState(false);
  const onlySelected = chatFilterMode === "selected";
  const listChats = onlySelected ? allowedChats : skippedChats;
  const [showSkipAdd, setShowSkipAdd] = useState(false);
  const [skipAddTab, setSkipAddTab] = useState("groups"); // "groups" | "individuals" | "manual"
  const [skipManualForm, setSkipManualForm] = useState({ chat_name: "", chat_id: "", chat_type: "individual" });
  const [availableChats, setAvailableChats] = useState([]);
  const [availableChatsLoading, setAvailableChatsLoading] = useState(false);
  const [availableChatsSearch, setAvailableChatsSearch] = useState("");

  // Timezone
  const [userTimezone, setUserTimezone] = useState("Asia/Kolkata");
  const [savingTimezone, setSavingTimezone] = useState(false);

  // Calendar invites + email reminders
  const [calendarPrefs, setCalendarPrefs] = useState({
    calendarInvites: "ask",
    calendarInviteAssignee: false,
    deadlineReminderMinutes: 30,
    dailyDigestEmail: false,
    dailyDigestHour: 8,
    reviewDigestEnabled: false,
    reviewDigestHours: 4,
    reviewDigestWhatsApp: true,
    reviewDigestEmail: true,
    deadlineDefaultHours: { urgent: 4, high: 24, medium: 48, low: 168 },
  });
  const [savingCalendarPrefs, setSavingCalendarPrefs] = useState(false);

  // Notification channels for this login (email / WhatsApp from the Brainydo number)
  const [prefs, setPrefs] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);

  // WhatsApp group notifications
  const [waNotify, setWaNotify] = useState({
    chatId: null,
    chatName: null,
    reminders: true,
    digest: true,
    newTasks: false,
    review: true,
    actAnyone: true,
  });
  const [savingWaNotify, setSavingWaNotify] = useState(false);

  // Assignees / team members
  const [assignees, setAssignees] = useState([]);
  const [addAssigneeForm, setAddAssigneeForm] = useState({ name: "", role: "" });
  const [addingAssignee, setAddingAssignee] = useState(false);

  // Business profile
  const [businessProfile, setBusinessProfile] = useState({ businessName: "", businessIndustry: "", businessDescription: "", opportunityKeywords: "" });
  const [savingBusinessProfile, setSavingBusinessProfile] = useState(false);

  // Custom classifiers
  const [classifiers, setClassifiers] = useState([]);
  const [showAddClassifier, setShowAddClassifier] = useState(false);
  const [newClassifier, setNewClassifier] = useState({ name: "", description: "" });
  const [classifierLoading, setClassifierLoading] = useState(false);

  // Connection Schedule
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFrom, setScheduleFrom] = useState("09:00");
  const [scheduleTo, setScheduleTo] = useState("18:00");
  const [scheduleDays, setScheduleDays] = useState([0, 1, 2, 3, 4]);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadUpgradePlans = async () => {
    setUpgradePlansLoading(true);
    try {
      const res = await api.get("/plans");
      setUpgradePlans(res.data.plans || []);
    } catch {
      setUpgradePlans([]);
    } finally {
      setUpgradePlansLoading(false);
    }
  };

  const handleOpenUpgrade = () => {
    setShowUpgradeModal(true);
    loadUpgradePlans();
  };

  useEffect(() => {
    loadChats();
    loadUserSettings();
    loadAssignees();
    loadSchedule();
    loadPrefs();
  }, []);

  const loadPrefs = async () => {
    try {
      const res = await api.get("/notifications/prefs");
      setPrefs(res.data);
    } catch { /* card shows a fallback */ }
  };

  const savePrefs = async (patch) => {
    try {
      const res = await api.post("/notifications/prefs", patch);
      setPrefs(res.data);
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save preferences");
    }
  };

  const sendPhoneCode = async () => {
    if (!phoneInput.trim()) return;
    setPhoneBusy(true);
    try {
      const res = await api.post("/notifications/phone", { phone: phoneInput.trim() });
      setPrefs(res.data);
      setCodeInput("");
      if (res.data.sent) toast.success(`Code sent to +${res.data.pending_phone} on WhatsApp`);
      else toast.success("Now send the WhatsApp message from that number to finish");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the code");
    } finally { setPhoneBusy(false); }
  };

  const verifyPhoneCode = async () => {
    if (!codeInput.trim()) return;
    setPhoneBusy(true);
    try {
      const res = await api.post("/notifications/phone/verify", { code: codeInput.trim() });
      setPrefs(res.data);
      setCodeInput(""); setPhoneInput("");
      toast.success("WhatsApp number verified — notifications will come from the Brainydo number");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify the code");
    } finally { setPhoneBusy(false); }
  };

  // While a verification is pending, poll so the card flips to "verified" the
  // moment the person's WhatsApp message reaches us.
  useEffect(() => {
    if (!prefs?.pending_phone || prefs?.phone_verified) return undefined;
    const id = setInterval(loadPrefs, 5000);
    return () => clearInterval(id);
  }, [prefs?.pending_phone, prefs?.phone_verified]); // eslint-disable-line react-hooks/exhaustive-deps

  const removePhone = async () => {
    const ok = await confirm({ title: "Remove this WhatsApp number?", message: "You'll stop getting Brainydo notifications on WhatsApp until you verify a number again.", confirmLabel: "Remove", tone: "danger" });
    if (!ok) return;
    try {
      const res = await api.delete("/notifications/phone");
      setPrefs(res.data);
    } catch { toast.error("Couldn't remove the number"); }
  };


  const loadSchedule = async () => {
    try {
      const res = await api.get("/settings/active-hours");
      setScheduleEnabled(res.data.enabled || false);
      setScheduleFrom(res.data.from_time || "09:00");
      setScheduleTo(res.data.to_time || "18:00");
      setScheduleDays(res.data.days || [0, 1, 2, 3, 4]);
    } catch {}
  };

  const loadUserSettings = async () => {
    try {
      const res = await api.get("/settings/user");
      if (res.data) {
        const fmt = res.data.dateFormat || "MM/DD/YYYY";
        setDateFormat(fmt);
        localStorage.setItem("dateFormat", fmt);
        setSkipGroups(res.data.skipGroups || false);
        setSkipIndividuals(res.data.skipIndividuals || false);
        setSkippedChats(res.data.skippedChats || []);
        setChatFilterMode(res.data.chatFilterMode === "selected" ? "selected" : "all");
        setAllowedChats(res.data.allowedChats || []);
        if (res.data.timezone) setUserTimezone(res.data.timezone);
        setCalendarPrefs((cur) => ({
          calendarInvites: res.data.calendarInvites || cur.calendarInvites,
          calendarInviteAssignee: !!res.data.calendarInviteAssignee,
          deadlineReminderMinutes: res.data.deadlineReminderMinutes ?? cur.deadlineReminderMinutes,
          dailyDigestEmail: !!res.data.dailyDigestEmail,
          dailyDigestHour: res.data.dailyDigestHour ?? cur.dailyDigestHour,
          reviewDigestEnabled: !!res.data.reviewDigestEnabled,
          reviewDigestHours: res.data.reviewDigestHours ?? cur.reviewDigestHours,
          reviewDigestWhatsApp: res.data.reviewDigestWhatsApp !== false,
          reviewDigestEmail: res.data.reviewDigestEmail !== false,
          deadlineDefaultHours: { ...cur.deadlineDefaultHours, ...(res.data.deadlineDefaultHours || {}) },
        }));
        if (res.data.calendarInvites) setCalendarInviteMode(res.data.calendarInvites);
        setWaNotify({
          chatId: res.data.waNotifyChatId || null,
          chatName: res.data.waNotifyChatName || null,
          reminders: res.data.waNotifyReminders !== false,
          digest: res.data.waNotifyDigest !== false,
          newTasks: !!res.data.waNotifyNewTasks,
          review: res.data.waNotifyReview !== false,
          actAnyone: res.data.waNotifyActAnyone !== false,
        });
        if (res.data.businessProfile) {
          setBusinessProfile({
            businessName: res.data.businessProfile.businessName || "",
            businessIndustry: res.data.businessProfile.businessIndustry || "",
            businessDescription: res.data.businessProfile.businessDescription || "",
            opportunityKeywords: res.data.businessProfile.opportunityKeywords || "",
          });
        }
      }
    } catch {
      // Settings not found, use defaults
    }
  };

  // The chat list card edits the skip list or, in "only selected" mode, the allow list.
  const listEndpoint = onlySelected ? "/settings/allowed-chats" : "/settings/skipped-chats";
  const listLabel = onlySelected ? "selected chats" : "skip list";

  const applyChatLists = (data) => {
    setSkippedChats(data.skippedChats || []);
    setAllowedChats(data.allowedChats || []);
  };

  const addToSkipList = async (chat) => {
    try {
      const res = await api.post(listEndpoint, {
        chat_id: chat.chat_id,
        chat_name: chat.chat_name || chat.name,
        chat_type: chat.chat_type,
      });
      applyChatLists(res.data);
      toast.success(`"${chat.chat_name || chat.name}" added to ${listLabel}`);
    } catch {
      toast.error(`Failed to update ${listLabel}`);
    }
  };

  const removeFromSkipList = async (chatId) => {
    try {
      const res = await api.delete(`${listEndpoint}/${encodeURIComponent(chatId)}`);
      applyChatLists(res.data);
      toast.success(`Removed from ${listLabel}`);
    } catch {
      toast.error(`Failed to update ${listLabel}`);
    }
  };

  const saveChatFilterMode = async (mode) => {
    if (mode === chatFilterMode) return;
    setSavingFilterMode(true);
    try {
      const res = await api.post("/settings/user", { chatFilterMode: mode });
      setChatFilterMode(res.data.chatFilterMode === "selected" ? "selected" : "all");
      applyChatLists(res.data);
      toast.success(mode === "selected" ? "Only selected chats will be processed" : "All chats will be processed");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingFilterMode(false);
    }
  };

  const addManualSkip = async () => {
    if (!skipManualForm.chat_name.trim() || !skipManualForm.chat_id.trim()) {
      toast.error("Chat name and Chat ID are required");
      return;
    }
    await addToSkipList(skipManualForm);
    setSkipManualForm({ chat_name: "", chat_id: "", chat_type: "individual" });
    setShowSkipAdd(false);
  };

  const loadAvailableChats = async () => {
    setAvailableChatsLoading(true);
    try {
      const res = await api.get("/settings/available-chats");
      setAvailableChats(res.data.chats || []);
    } catch {
      toast.error("Failed to load chats from message history");
    } finally {
      setAvailableChatsLoading(false);
    }
  };

  const openSkipAdd = () => {
    setShowSkipAdd(true);
    setSkipAddTab("groups");
    setAvailableChatsSearch("");
    loadAvailableChats();
  };

  const saveUserSettings = async () => {
    setSavingSkipSettings(true);
    try {
      await api.post("/settings/user", {
        skipGroups,
        skipIndividuals,
      });
      toast.success("Skip settings updated");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingSkipSettings(false);
    }
  };

  const loadAssignees = async () => {
    try {
      const res = await api.get("/settings/assignees");
      setAssignees(res.data.assignees || []);
    } catch { /* silent */ }
  };

  const addAssignee = async () => {
    if (!addAssigneeForm.name.trim()) { toast.error("Name is required"); return; }
    setAddingAssignee(true);
    try {
      const res = await api.post("/settings/assignees", {
        name: addAssigneeForm.name.trim(),
        role: addAssigneeForm.role.trim() || null,
      });
      setAssignees((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setAddAssigneeForm({ name: "", role: "" });
      toast.success("Team member added");
    } catch { toast.error("Failed to add"); }
    finally { setAddingAssignee(false); }
  };

  const deleteAssignee = async (id) => {
    try {
      await api.delete(`/settings/assignees/${id}`);
      setAssignees((prev) => prev.filter((a) => a.id !== id));
      toast.success("Removed");
    } catch { toast.error("Failed to remove"); }
  };

  const saveBusinessProfile = async () => {
    setSavingBusinessProfile(true);
    try {
      await api.post("/settings/user", { businessProfile });
      toast.success("Business profile saved");
    } catch {
      toast.error("Failed to save business profile");
    } finally {
      setSavingBusinessProfile(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.post("/settings/active-hours", {
        enabled: scheduleEnabled,
        from_time: scheduleFrom,
        to_time: scheduleTo,
        timezone: "Asia/Kolkata",
        days: scheduleDays,
      });
      toast.success("Schedule saved");
    } catch {
      toast.error("Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const saveTimezone = async (tz) => {
    setUserTimezone(tz);
    setSavingTimezone(true);
    try {
      await api.post("/settings/user", { timezone: tz });
      toast.success("Timezone updated");
    } catch {
      toast.error("Failed to save timezone");
    } finally {
      setSavingTimezone(false);
    }
  };

  const saveCalendarPrefs = async (patch) => {
    const next = { ...calendarPrefs, ...patch };
    setCalendarPrefs(next);
    setSavingCalendarPrefs(true);
    try {
      await api.post("/settings/user", patch);
      if (patch.calendarInvites) setCalendarInviteMode(patch.calendarInvites);
      toast.success("Reminder settings saved");
    } catch {
      toast.error("Failed to save reminder settings");
    } finally {
      setSavingCalendarPrefs(false);
    }
  };

  // ── WhatsApp group notifications ──


  const saveWaNotify = async (patch, successMsg = "WhatsApp notification settings saved") => {
    const prev = waNotify;
    setWaNotify((cur) => ({ ...cur, ...patch }));
    setSavingWaNotify(true);
    try {
      const body = {};
      if ("chatId" in patch) {
        body.waNotifyChatId = patch.chatId || "";
        body.waNotifyChatName = patch.chatName || "";
      }
      if ("reminders" in patch) body.waNotifyReminders = patch.reminders;
      if ("digest" in patch) body.waNotifyDigest = patch.digest;
      if ("newTasks" in patch) body.waNotifyNewTasks = patch.newTasks;
      if ("review" in patch) body.waNotifyReview = patch.review;
      if ("actAnyone" in patch) body.waNotifyActAnyone = patch.actAnyone;
      const res = await api.post("/settings/user", body);
      toast.success(successMsg);
      return true;
    } catch (e) {
      setWaNotify(prev);
      toast.error(e?.response?.data?.detail || "Failed to save WhatsApp notification settings");
      return false;
    } finally {
      setSavingWaNotify(false);
    }
  };




  const saveDateFormat = async (format) => {
    setDateFormat(format);
    setSavingDateFormat(true);
    try {
      localStorage.setItem("dateFormat", format);
      await api.post("/settings/user", { dateFormat: format });
      toast.success("Date format updated");
    } catch {
      toast.error("Failed to save date format");
    } finally {
      setSavingDateFormat(false);
    }
  };

  const loadClassifiers = async () => {
    try {
      const res = await api.get("/settings/classifiers");
      setClassifiers(res.data.classifiers || []);
    } catch {
      toast.error("Failed to load custom classifiers");
    }
  };

  useEffect(() => {
    loadClassifiers();
  }, []);

  const loadChats = async () => {
    try {
      const res = await api.get("/settings/chats");
      setChats(res.data.chats || []);
    } catch {
      toast.error("Failed to load chat presets");
    } finally {
      setLoading(false);
    }
  };

  const handleAddChat = async () => {
    if (!addForm.name || !addForm.chat_id) {
      toast.error("Name and Chat ID are required");
      return;
    }
    setAddLoading(true);
    try {
      const res = await api.post("/settings/chats", {
        name: addForm.name,
        chat_id: addForm.chat_id,
        chat_type: addForm.chat_type,
        phone: addForm.phone || null,
      });
      setChats([res.data, ...chats]);
      setAddForm({ name: "", chat_id: "", chat_type: "individual", phone: "" });
      setShowAdd(false);
      toast.success("Chat preset added");
    } catch {
      toast.error("Failed to add chat preset");
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = (chat) => {
    setEditId(chat.id);
    setEditForm({
      name: chat.name,
      chat_id: chat.chat_id,
      chat_type: chat.chat_type,
      phone: chat.phone || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.chat_id) {
      toast.error("Name and Chat ID are required");
      return;
    }
    try {
      const res = await api.put(`/settings/chats/${editId}`, {
        name: editForm.name,
        chat_id: editForm.chat_id,
        chat_type: editForm.chat_type,
        phone: editForm.phone || null,
      });
      setChats(chats.map((c) => (c.id === editId ? res.data : c)));
      setEditId(null);
      toast.success("Chat preset updated");
    } catch {
      toast.error("Failed to update chat preset");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete this chat preset?",
      message: "The shortcut will be removed. Messages sent to this chat stay.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/settings/chats/${id}`);
      setChats(chats.filter((c) => c.id !== id));
      toast.success("Chat preset deleted");
    } catch {
      toast.error("Failed to delete chat preset");
    }
  };

  const handleAddClassifier = async () => {
    if (!newClassifier.name.trim()) {
      toast.error("Classifier name is required");
      return;
    }
    setClassifierLoading(true);
    try {
      const res = await api.post("/settings/classifiers", {
        name: newClassifier.name,
        description: newClassifier.description,
      });
      setClassifiers([res.data, ...classifiers]);
      setNewClassifier({ name: "", description: "" });
      setShowAddClassifier(false);
      toast.success("Custom classifier added");
    } catch {
      toast.error("Failed to add classifier");
    } finally {
      setClassifierLoading(false);
    }
  };

  const handleDeleteClassifier = async (id) => {
    const ok = await confirm({
      title: "Delete this custom classifier?",
      message: "Existing messages keep their classifications; future messages won't match this rule.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/settings/classifiers/${id}`);
      setClassifiers(classifiers.filter((c) => c.id !== id));
      toast.success("Classifier deleted");
    } catch {
      toast.error("Failed to delete classifier");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Settings</h1>
          <p className="text-[14px] text-slate-500 mt-1">Manage your WhatsApp intelligence preferences and AI behavior.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="h-10 px-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95"
        >
          <Plus size={14} />
          Add Chat
        </button>
      </div>

      {/* Lead Detection + Channel + Team — separate dedicated pages */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <Link
          to="/app/settings/leads"
          className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg grid place-items-center shrink-0">
              <Webhook size={17} className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-slate-900">Lead Detection &amp; CRM</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Rules, webhook, Slack, email — and which messages get treated as leads.
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-900 transition" />
          </div>
        </Link>

        <Link
          to="/app/settings/channel"
          className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-violet-50 border border-violet-100 rounded-lg grid place-items-center shrink-0">
              <Smartphone size={17} className="text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-slate-900">WhatsApp Channel</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Choose how WhatsApp connects: QR pairing or Business API (Jalpi).
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-900 transition" />
          </div>
        </Link>

        <Link
          to="/app/settings/integrations"
          className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition"
          data-testid="settings-integrations-link"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-lg grid place-items-center shrink-0">
              <CheckCircle2 size={17} className="text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-slate-900">Integrations</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Sync tasks with Microsoft Planner.
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-900 transition" />
          </div>
        </Link>

        <Link
          to="/app/settings/team"
          className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-50 border border-emerald-100 rounded-lg grid place-items-center shrink-0">
              <Users size={17} className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-slate-900">Team &amp; Permissions</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Invite colleagues, assign roles, share access to this workspace.
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-900 transition" />
          </div>
        </Link>

        <Link
          to="/app/settings/password"
          className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-50 border border-amber-100 rounded-lg grid place-items-center shrink-0">
              <KeyRound size={17} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-slate-900">Change Password</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Update the password you use to sign in to your account.
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-900 transition" />
          </div>
        </Link>
      </div>

      {/* Subscription & Plan */}
      {user?.subscription && (() => {
        const sub = user.subscription;
        const meta = SUB_STATUS_META[sub.status] || { label: sub.status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
        const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg grid place-items-center shrink-0">
                <CreditCard size={15} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Subscription & Plan</h2>
                <p className="text-[13px] text-slate-600 font-medium">{sub.plan_name || "Trial Plan"}</p>
              </div>
              <span className={`ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-[11.5px] font-semibold border ${meta.cls}`}>
                {meta.label}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Plan</div>
                <div className="text-[14px] font-bold text-slate-900">{sub.plan_name || "Trial Plan"}</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
                  {sub.status === "trial" || sub.status === "trial_expired" ? "Trial Expires" : "Plan Expires"}
                </div>
                <div className={`text-[14px] font-bold ${!sub.expires_at ? "text-slate-400" : "text-slate-900"}`}>
                  {fmtDate(sub.expires_at) || "Unlimited"}
                </div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Days Remaining</div>
                <div className={`text-[14px] font-bold ${
                  sub.days_remaining === 9999 ? "text-emerald-600" :
                  sub.days_remaining <= 5 ? "text-red-600" :
                  sub.days_remaining <= 14 ? "text-amber-600" : "text-slate-900"
                }`}>
                  {sub.days_remaining === 9999 ? "Unlimited" : sub.days_remaining === 0 ? "Expired" : `${sub.days_remaining} days`}
                </div>
              </div>
            </div>

            {(sub.status === "trial_expired" || sub.status === "expired" || sub.status === "suspended" || (sub.days_remaining !== 9999 && sub.days_remaining <= 5)) && (
              <div className={`p-4 rounded-xl border text-[13px] flex items-start gap-3 ${
                sub.status === "suspended" ? "bg-slate-50 border-slate-200 text-slate-700" :
                "bg-amber-50 border-amber-200 text-amber-900"
              }`}>
                <span className="material-symbols-outlined text-[18px] mt-0.5 shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {sub.status === "suspended" ? "block" : "warning"}
                </span>
                <div>
                  <div className="font-semibold mb-0.5">
                    {sub.status === "suspended" ? "Account Suspended" :
                     sub.status === "trial_expired" ? "Trial Period Ended" :
                     sub.status === "expired" ? "Subscription Expired" :
                     `${sub.days_remaining} day${sub.days_remaining !== 1 ? "s" : ""} remaining`}
                  </div>
                  <div className="text-[12px] opacity-80">
                    {sub.status === "suspended"
                      ? "Your account has been suspended. Contact your administrator to restore access."
                      : "To continue using Brainydo without interruption, please contact your administrator to upgrade or renew your plan."}
                  </div>
                  <a
                    href="mailto:admin@brainydo.com"
                    className="inline-block mt-2 h-8 px-4 bg-slate-900 text-white rounded-lg text-[12px] font-semibold hover:bg-slate-700 transition leading-8"
                  >
                    Contact Admin to Upgrade
                  </a>
                </div>
              </div>
            )}

            <button
              onClick={handleOpenUpgrade}
              className="mt-4 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition text-sm"
            >
              {sub?.status === "active" ? "Renew / Change Plan" : "Upgrade to Paid Plan"}
            </button>
          </div>
        );
      })()}

      {/* Chat Presets Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Chat Presets</h2>
            <p className="text-[13px] text-slate-600 mt-1 font-medium">{chats.length} active preset{chats.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 text-[13px] py-8">Loading presets...</div>
        ) : chats.length === 0 && !showAdd ? (
          <div className="text-center text-slate-500 text-[13px] py-8">No chat presets yet</div>
        ) : (
          <>
            {/* Add Form */}
            {showAdd && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Chat Name"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    className="h-9 px-3 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                  <input
                    type="text"
                    placeholder="Chat ID"
                    value={addForm.chat_id}
                    onChange={(e) => setAddForm({ ...addForm, chat_id: e.target.value })}
                    className="h-9 px-3 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                  <select
                    value={addForm.chat_type}
                    onChange={(e) => setAddForm({ ...addForm, chat_type: e.target.value })}
                    className="h-9 px-3 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  >
                    <option value="individual">Individual</option>
                    <option value="group">Group</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Phone (optional)"
                    value={addForm.phone}
                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                    className="h-9 px-3 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddChat}
                    disabled={addLoading}
                    className="h-8 px-3 bg-blue-600 text-white rounded-lg text-[13px] hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {addLoading ? "Adding..." : "Add"}
                  </button>
                  <button
                    onClick={() => setShowAdd(false)}
                    className="h-8 px-3 border border-slate-200 rounded-sm text-[13px] hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Chats Table */}
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                      <th className="px-4 py-2 text-left text-slate-600 font-medium">Chat ID</th>
                      <th className="px-4 py-2 text-left text-slate-600 font-medium">Type</th>
                      <th className="px-4 py-2 text-left text-slate-600 font-medium">Phone</th>
                      <th className="px-4 py-2 text-right text-slate-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chats.map((chat) => (
                      <tr key={chat.id} className="border-t border-slate-100 hover:bg-slate-50">
                        {editId === chat.id ? (
                          <>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="w-full h-7 px-2 border border-slate-200 rounded-sm text-[12px]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editForm.chat_id}
                                onChange={(e) => setEditForm({ ...editForm, chat_id: e.target.value })}
                                className="w-full h-7 px-2 border border-slate-200 rounded-sm text-[12px]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                value={editForm.chat_type}
                                onChange={(e) => setEditForm({ ...editForm, chat_type: e.target.value })}
                                className="w-full h-7 px-2 border border-slate-200 rounded-sm text-[12px]"
                              >
                                <option value="individual">Individual</option>
                                <option value="group">Group</option>
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editForm.phone}
                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                className="w-full h-7 px-2 border border-slate-200 rounded-sm text-[12px]"
                              />
                            </td>
                            <td className="px-4 py-2 text-right space-x-1">
                              <button
                                onClick={handleSaveEdit}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-[11px] hover:bg-blue-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditId(null)}
                                className="px-2 py-1 border border-slate-200 rounded-sm text-[11px] hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 text-slate-900 font-medium">{chat.name}</td>
                            <td className="px-4 py-2 text-slate-600">{chat.chat_id}</td>
                            <td className="px-4 py-2">
                              <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 rounded-sm text-[11px] capitalize">
                                {chat.chat_type}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-600">{chat.phone || "—"}</td>
                            <td className="px-4 py-2 text-right space-x-1">
                              <button
                                onClick={() => handleEdit(chat)}
                                className="px-2 py-1 border border-slate-200 rounded-sm text-[11px] hover:bg-slate-50"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(chat.id)}
                                className="px-2 py-1 border border-red-200 text-red-600 rounded-sm text-[11px] hover:bg-red-50"
                              >
                                <Trash2 size={12} className="inline" />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Timezone Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Timezone</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Your timezone is sent with every message so the AI schedules deadlines and follow-ups in your local time.</p>
        </div>
        <select
          value={userTimezone}
          onChange={(e) => saveTimezone(e.target.value)}
          disabled={savingTimezone}
          className="w-full max-w-md h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-60 transition"
        >
          {[
            { tz: "Asia/Kolkata",         label: "India — IST (UTC+05:30)" },
            { tz: "Asia/Karachi",         label: "Pakistan — PKT (UTC+05:00)" },
            { tz: "Asia/Dhaka",           label: "Bangladesh — BST (UTC+06:00)" },
            { tz: "Asia/Colombo",         label: "Sri Lanka — SLST (UTC+05:30)" },
            { tz: "Asia/Kathmandu",       label: "Nepal — NPT (UTC+05:45)" },
            { tz: "Asia/Dubai",           label: "UAE / Dubai — GST (UTC+04:00)" },
            { tz: "Asia/Riyadh",          label: "Saudi Arabia — AST (UTC+03:00)" },
            { tz: "Asia/Singapore",       label: "Singapore — SGT (UTC+08:00)" },
            { tz: "Asia/Kuala_Lumpur",    label: "Malaysia — MYT (UTC+08:00)" },
            { tz: "Asia/Bangkok",         label: "Thailand — ICT (UTC+07:00)" },
            { tz: "Asia/Jakarta",         label: "Indonesia — WIB (UTC+07:00)" },
            { tz: "Asia/Tokyo",           label: "Japan — JST (UTC+09:00)" },
            { tz: "Asia/Shanghai",        label: "China — CST (UTC+08:00)" },
            { tz: "Asia/Seoul",           label: "South Korea — KST (UTC+09:00)" },
            { tz: "Asia/Taipei",          label: "Taiwan — CST (UTC+08:00)" },
            { tz: "Asia/Hong_Kong",       label: "Hong Kong — HKT (UTC+08:00)" },
            { tz: "Asia/Kabul",           label: "Afghanistan — AFT (UTC+04:30)" },
            { tz: "Asia/Tashkent",        label: "Uzbekistan — UZT (UTC+05:00)" },
            { tz: "Europe/London",        label: "UK — GMT/BST (UTC+00:00/+01:00)" },
            { tz: "Europe/Paris",         label: "France / Germany — CET (UTC+01:00)" },
            { tz: "Europe/Moscow",        label: "Russia — MSK (UTC+03:00)" },
            { tz: "Africa/Johannesburg",  label: "South Africa — SAST (UTC+02:00)" },
            { tz: "Africa/Nairobi",       label: "Kenya / EAT (UTC+03:00)" },
            { tz: "Africa/Lagos",         label: "Nigeria — WAT (UTC+01:00)" },
            { tz: "America/New_York",     label: "US East — EST/EDT (UTC-05:00)" },
            { tz: "America/Chicago",      label: "US Central — CST/CDT (UTC-06:00)" },
            { tz: "America/Denver",       label: "US Mountain — MST/MDT (UTC-07:00)" },
            { tz: "America/Los_Angeles",  label: "US West — PST/PDT (UTC-08:00)" },
            { tz: "America/Toronto",      label: "Canada East — EST/EDT (UTC-05:00)" },
            { tz: "America/Vancouver",    label: "Canada West — PST/PDT (UTC-08:00)" },
            { tz: "America/Sao_Paulo",    label: "Brazil — BRT (UTC-03:00)" },
            { tz: "Australia/Sydney",     label: "Australia East — AEST/AEDT (UTC+10:00)" },
            { tz: "Australia/Perth",      label: "Australia West — AWST (UTC+08:00)" },
            { tz: "Pacific/Auckland",     label: "New Zealand — NZST/NZDT (UTC+12:00)" },
            { tz: "UTC",                  label: "UTC (UTC+00:00)" },
          ].map(({ tz, label }) => (
            <option key={tz} value={tz}>{label}</option>
          ))}
        </select>
        {savingTimezone && <p className="text-[12px] text-slate-400 mt-2">Saving…</p>}
      </div>

      {/* Security: inactivity logout */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="idle-logout-card">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Security · Sign-in</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Log me out of Brainydo on this browser after a period with no activity. You get a 60-second warning first.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[[0, "Never"], [15, "15 min"], [30, "30 min"], [60, "1 hour"], [240, "4 hours"], [480, "8 hours"]].map(([m, label]) => {
            const active = (Number(user?.idleLogoutMinutes) || 0) === m;
            return (
              <button key={m} type="button" disabled={savingIdle} onClick={() => !active && saveIdleLogout(m)}
                className={"h-9 px-4 rounded-xl border text-[13px] font-semibold transition disabled:opacity-60 " +
                  (active ? "bg-blue-600 border-blue-600 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400")}
                data-testid={`idle-logout-${m}`}>
                {label}
              </button>
            );
          })}
        </div>
        <p className="text-[12px] text-slate-400 mt-3">
          {(Number(user?.idleLogoutMinutes) || 0) === 0
            ? "Default: you stay logged in until you log out yourself. WhatsApp keeps working either way."
            : "Applies to your login only. WhatsApp keeps reading messages and sending reminders after you're logged out."}
        </p>

        <div className="mt-6 pt-5 border-t border-slate-100 flex items-start justify-between gap-4" data-testid="passwordless-row">
          <div>
            <div className="text-[13.5px] font-semibold text-slate-900">Passwordless login</div>
            <p className="text-[12.5px] text-slate-500 mt-0.5 max-w-xl">
              Sign in with a 6-digit code sent to your email{prefs?.phone_verified ? " and WhatsApp" : ""} instead of typing your password.
              Your password keeps working too.
              {!prefs?.phone_verified && " Verify your WhatsApp number under Notification channels to also get codes there."}
            </p>
            <p className="text-[12px] text-slate-400 mt-1.5">
              Links in Brainydo's WhatsApp notifications always sign you in with one tap — each link works once and expires after 48 hours.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
            <input type="checkbox" checked={!!user?.passwordlessLogin} disabled={savingIdle}
              onChange={(e) => savePasswordless(e.target.checked)} className="sr-only peer" data-testid="passwordless-toggle" />
            <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </label>
        </div>
      </div>

      {/* Calendar invites & email reminders */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="calendar-reminders-section">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Calendar &amp; Reminders</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Get task deadlines into your own calendar and inbox, so nothing slips when Brainydo isn't open.</p>
        </div>

        {/* Invite mode */}
        <div className="mb-6">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2">
            <CalendarPlus size={13} className="text-blue-600" /> Calendar invites for deadlines
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { value: "ask",    label: "Ask each time",  desc: "The deadline picker shows a checkbox. You decide per task." },
              { value: "always", label: "Always",         desc: "Every deadline is emailed as an invite, including ones the AI extracts from chats." },
              { value: "never",  label: "Never",          desc: "Only the “Add to calendar” button on a task sends an invite." },
            ].map((opt) => {
              const sel = calendarPrefs.calendarInvites === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => saveCalendarPrefs({ calendarInvites: opt.value })}
                  disabled={savingCalendarPrefs}
                  className={`p-4 border-2 rounded-xl text-left transition ${sel ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-blue-400 bg-white"}`}
                  data-testid={`calendar-invites-${opt.value}`}
                >
                  <div className={`text-[13px] font-bold ${sel ? "text-blue-700" : "text-slate-800"}`}>{opt.label}</div>
                  <div className="text-[12px] text-slate-500 mt-1 leading-snug">{opt.desc}</div>
                </button>
              );
            })}
          </div>
          <label className="mt-3 flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={calendarPrefs.calendarInviteAssignee}
              onChange={(e) => saveCalendarPrefs({ calendarInviteAssignee: e.target.checked })}
              disabled={savingCalendarPrefs}
              className="h-4 w-4 accent-blue-600"
              data-testid="calendar-invite-assignee"
            />
            <span className="text-[13px] text-slate-700">Also send invites and reminders to the task's assignee <span className="text-slate-400">(needs an email on the team member)</span></span>
          </label>
          <p className="text-[12px] text-slate-400 mt-2">Invites arrive as a standard .ics file, which Google Calendar, Outlook and Apple Calendar all open. Rescheduling a task updates the same event; completing or deleting it cancels the event.</p>
        </div>

        {/* Reminder lead time */}
        <div className="mb-6">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2">
            <BellRing size={13} className="text-amber-600" /> Reminder before a deadline
          </div>
          <select
            value={calendarPrefs.deadlineReminderMinutes}
            onChange={(e) => saveCalendarPrefs({ deadlineReminderMinutes: Number(e.target.value) })}
            disabled={savingCalendarPrefs}
            className="w-full max-w-xs h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-60 transition"
            data-testid="deadline-reminder-minutes"
          >
            {[
              { v: 0,    label: "Off" },
              { v: 15,   label: "15 minutes before" },
              { v: 30,   label: "30 minutes before" },
              { v: 60,   label: "1 hour before" },
              { v: 120,  label: "2 hours before" },
              { v: 240,  label: "4 hours before" },
              { v: 1440, label: "1 day before" },
            ].map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <p className="text-[12px] text-slate-400 mt-2">One reminder per task by email and, if you've mapped a group below, on WhatsApp. Sent even if you never opened the calendar invite; re-arms automatically when a deadline is rescheduled.</p>
        </div>

        {/* Daily digest */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2">
            <Mail size={13} className="text-slate-600" /> Daily task digest
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={calendarPrefs.dailyDigestEmail}
                onChange={(e) => saveCalendarPrefs({ dailyDigestEmail: e.target.checked })}
                disabled={savingCalendarPrefs}
                className="h-4 w-4 accent-blue-600"
                data-testid="daily-digest-toggle"
              />
              <span className="text-[13px] text-slate-700">Email me overdue and due-today tasks every morning</span>
            </label>
            <div className="flex items-center gap-2 text-[13px] text-slate-600">
              at
              <select
                value={calendarPrefs.dailyDigestHour}
                onChange={(e) => saveCalendarPrefs({ dailyDigestHour: Number(e.target.value) })}
                disabled={savingCalendarPrefs || !calendarPrefs.dailyDigestEmail}
                className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] bg-white disabled:opacity-50"
                data-testid="daily-digest-hour"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}</option>
                ))}
              </select>
              <span className="text-slate-400">({userTimezone})</span>
            </div>
          </div>
          <p className="text-[12px] text-slate-400 mt-2">Skipped on days with nothing due. The same digest is posted to your WhatsApp notification group at this hour when one is mapped.</p>
        </div>

        {/* Review queue list */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2">
            <Mail size={13} className="text-slate-600" /> Review queue list
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={calendarPrefs.reviewDigestEnabled}
                onChange={(e) => saveCalendarPrefs({ reviewDigestEnabled: e.target.checked })}
                disabled={savingCalendarPrefs}
                className="h-4 w-4 accent-blue-600"
                data-testid="review-digest-toggle"
              />
              <span className="text-[13px] text-slate-700">Send me the review list when items wait longer than</span>
            </label>
            <select
              value={calendarPrefs.reviewDigestHours}
              onChange={(e) => saveCalendarPrefs({ reviewDigestHours: Number(e.target.value) })}
              disabled={savingCalendarPrefs || !calendarPrefs.reviewDigestEnabled}
              className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] bg-white disabled:opacity-50"
              data-testid="review-digest-hours"
            >
              {[1, 2, 3, 4, 6, 8, 12, 24].map((h) => (
                <option key={h} value={h}>{h} hour{h > 1 ? "s" : ""}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-5 mt-3 pl-6">
            {[["reviewDigestWhatsApp", "WhatsApp (Brainydo number)"], ["reviewDigestEmail", "Email"]].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer select-none text-[13px] text-slate-700">
                <input
                  type="checkbox"
                  checked={calendarPrefs[key]}
                  onChange={(e) => saveCalendarPrefs({ [key]: e.target.checked })}
                  disabled={savingCalendarPrefs || !calendarPrefs.reviewDigestEnabled}
                  className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                />
                {label}
              </label>
            ))}
          </div>
          <p className="text-[12px] text-slate-400 mt-2">
            The newest 20 items in one message, repeated at this interval while anything waits, during your working hours.
            Reply once on WhatsApp: <span className="font-semibold text-slate-600">A ALL</span>, <span className="font-semibold text-slate-600">R ALL</span>, or
            {" "}<span className="font-semibold text-slate-600">A822, R823</span> to decide each; <span className="font-semibold text-slate-600">LIST 2</span> / <span className="font-semibold text-slate-600">MORE</span> for the next page.
            WhatsApp needs a verified number with WhatsApp notifications on (below).
          </p>
        </div>
        {/* Default deadlines */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-1">
            <Calendar size={13} className="text-slate-600" /> Default deadline when none is mentioned
          </div>
          <p className="text-[12px] text-slate-500 mb-3">Every task gets a deadline. When a chat doesn't mention one, or you approve or create a task without a date, Brainydo sets it from the priority. Those show an <span className="font-mono text-[10px] uppercase bg-slate-100 border border-slate-200 rounded px-1">auto</span> tag so you know to adjust them.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "urgent", label: "Urgent", cls: "text-red-700" },
              { key: "high",   label: "High",   cls: "text-amber-700" },
              { key: "medium", label: "Medium", cls: "text-blue-700" },
              { key: "low",    label: "Low",    cls: "text-slate-600" },
            ].map((p) => (
              <label key={p.key} className="block">
                <span className={"block text-[11px] font-bold uppercase tracking-wider mb-1 " + p.cls}>{p.label}</span>
                <select
                  value={calendarPrefs.deadlineDefaultHours?.[p.key] ?? 48}
                  onChange={(e) => saveCalendarPrefs({ deadlineDefaultHours: { ...calendarPrefs.deadlineDefaultHours, [p.key]: Number(e.target.value) } })}
                  disabled={savingCalendarPrefs}
                  className="w-full h-9 px-2 border border-slate-200 rounded-lg text-[13px] bg-white disabled:opacity-50"
                  data-testid={`deadline-default-${p.key}`}
                >
                  {[
                    { v: 1, l: "1 hour" }, { v: 2, l: "2 hours" }, { v: 4, l: "4 hours" }, { v: 8, l: "8 hours" },
                    { v: 24, l: "1 day" }, { v: 48, l: "2 days" }, { v: 72, l: "3 days" }, { v: 168, l: "1 week" }, { v: 336, l: "2 weeks" },
                  ].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </label>
            ))}
          </div>
          <p className="text-[12px] text-slate-400 mt-2">Under a day keeps the exact offset within 8 AM–8 PM; a day or more lands at 6 PM local time. Auto deadlines land on the next free slot — set your working hours, block busy time and connect other calendars on the <Link to="/app/calendar" className="text-blue-600 font-semibold underline">Calendar</Link> page.</p>
        </div>
        {savingCalendarPrefs && <p className="text-[12px] text-slate-400 mt-3">Saving…</p>}
      </div>

      {/* Notification channels */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="notify-channels-section">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">How you get notified</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Reminders, review items, new tasks and the daily digest can reach you by email and on WhatsApp from Brainydo's own number. Each person on the account chooses for themselves.</p>
        </div>
        {!prefs ? (
          <div className="text-[13px] text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-start gap-3 p-3.5 border border-slate-200 rounded-xl cursor-pointer hover:border-slate-400 transition">
              <input type="checkbox" checked={prefs.notifyEmail} onChange={(e) => savePrefs({ notifyEmail: e.target.checked })} className="h-4 w-4 accent-blue-600 mt-0.5" data-testid="pref-email" />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900"><Mail size={14} className="text-slate-500" /> Email</span>
                <span className="block text-[12px] text-slate-500 mt-0.5">To {prefs.email}. Calendar invites always go by email.</span>
              </span>
            </label>

            <div className={"p-3.5 border rounded-xl transition " + (prefs.notifyWhatsApp ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200")} data-testid="pref-whatsapp-card">
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={prefs.notifyWhatsApp} disabled={!prefs.phone_verified || !prefs.platform_available}
                  onChange={(e) => savePrefs({ notifyWhatsApp: e.target.checked })} className="h-4 w-4 accent-emerald-600 mt-0.5 disabled:opacity-50" data-testid="pref-whatsapp" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900"><MessageCircle size={14} className="text-emerald-600" /> WhatsApp from Brainydo</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    Messages arrive from the Brainydo number{prefs.sender_number ? <> (+{prefs.sender_number})</> : ""} in a private chat. Reply <span className="font-mono">A12</span>, <span className="font-mono">R12</span> or <span className="font-mono">D12</span> to act, or just ask a question. Replying keeps a 24-hour chat window open so you get full-detail messages; otherwise you get short approved-template versions.
                  </div>
                  {!prefs.platform_available ? (
                    <div className="mt-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Not available yet — the Brainydo number is being set up. Email keeps working in the meantime.</div>
                  ) : prefs.phone_verified ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px]">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 bg-white border border-emerald-200 rounded-full px-2.5 py-1"><CheckCircle2 size={13} /> +{prefs.phone} verified</span>
                      <button onClick={removePhone} className="text-slate-500 hover:text-red-600 underline-offset-2 hover:underline">Change number</button>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2" data-testid="phone-verify">
                      <div className="flex flex-wrap gap-2">
                        <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} placeholder="Your WhatsApp number, e.g. +91 98765 43210"
                          className="h-9 px-3 border border-slate-200 rounded-lg text-[13px] flex-1 min-w-[220px] focus:border-slate-900 outline-none" data-testid="phone-input" />
                        <button onClick={sendPhoneCode} disabled={phoneBusy || !phoneInput.trim()} className="h-9 px-3.5 rounded-lg bg-slate-900 text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                          {phoneBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {prefs.pending_phone ? "Change number" : "Verify this number"}
                        </button>
                      </div>
                      {prefs.pending_phone && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="verify-pending">
                          <div className="text-[12.5px] text-slate-800">
                            <span className="font-semibold">Step 2:</span> from <span className="font-mono">+{prefs.pending_phone}</span>, send any WhatsApp message to the Brainydo number <span className="font-mono">+{prefs.sender_number}</span>. We verify it the moment it arrives.
                          </div>
                          {prefs.wa_link && (
                            <a href={prefs.wa_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold" data-testid="wa-me-link">
                              <MessageCircle size={13} /> Open WhatsApp and send it
                            </a>
                          )}
                          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-emerald-100">
                            <span className="text-[11.5px] text-slate-500">Got a code instead? Enter it:</span>
                            <input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456"
                              className="h-8 w-24 px-2.5 border border-slate-200 rounded-lg text-[12.5px] font-mono tracking-widest focus:border-slate-900 outline-none bg-white" data-testid="code-input" />
                            <button onClick={verifyPhoneCode} disabled={phoneBusy || codeInput.trim().length < 4} className="h-8 px-3 rounded-lg border border-slate-300 bg-white text-[12px] font-semibold disabled:opacity-50">Verify code</button>
                            <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> waiting…</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* What Brainydo sends, and who may act by replying */}
            <div className="pt-4 border-t border-slate-100">
              <div className="text-[12px] font-semibold text-slate-700 mb-2">What Brainydo sends</div>
              <div className="space-y-2.5">
                {[
                  { key: "review",    label: "Review queue items", desc: "Each item comes with a number. Reply A12 to approve or R12 to reject." },
                  { key: "reminders", label: "Deadline reminders", desc: "Same timing as the email reminder. Reply D12 to mark the task done." },
                  { key: "digest",    label: "Daily task digest",  desc: "Overdue and due-today tasks each morning at the digest hour." },
                  { key: "newTasks",  label: "New tasks",          desc: "When Brainydo auto-creates a task from a chat without needing review." },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={!!waNotify[opt.key]} onChange={(e) => saveWaNotify({ [opt.key]: e.target.checked })} disabled={savingWaNotify}
                      className="h-4 w-4 accent-emerald-600 mt-0.5" data-testid={`whatsapp-notify-${opt.key}`} />
                    <span className="text-[13px] text-slate-700">{opt.label} <span className="text-slate-400">— {opt.desc}</span></span>
                  </label>
                ))}
              </div>
              <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={!!waNotify.actAnyone} onChange={(e) => saveWaNotify({ actAnyone: e.target.checked })} disabled={savingWaNotify}
                  className="h-4 w-4 accent-emerald-600 mt-0.5" data-testid="whatsapp-notify-actAnyone" />
                <span className="text-[13px] text-slate-700">Team members can approve, reject or complete tasks by replying <span className="text-slate-400">— untick to accept task actions only from you. Every action is recorded on the task with the person's name.</span></span>
              </label>
              <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-[12px] text-slate-600 leading-relaxed">
                <div className="font-semibold text-slate-700 mb-1">Reply cheat-sheet</div>
                <span className="font-mono text-slate-800">A12</span> approve · <span className="font-mono text-slate-800">R12</span> reject · <span className="font-mono text-slate-800">D12</span> done · <span className="font-mono text-slate-800">A12 13 14</span> several at once. Or just write what you want: “mark the Ramesh task done”, “what's overdue?”, “add a task: send invoice to Regenta by Friday”.
              </div>
            </div>
          </div>
        )}
        {savingWaNotify && <p className="text-[12px] text-slate-400 mt-3">Saving…</p>}
      </div>

      {/* Date Format Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Date Format</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Choose how dates are displayed in your tasks</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { format: "MM/DD/YYYY", label: "Standard (US)", example: "12/25/2024" },
            { format: "DD/MM/YYYY", label: "International", example: "25/12/2024" },
            { format: "YYYY-MM-DD", label: "Scientific", example: "2024-12-25" },
            { format: "DD-MMM-YYYY", label: "Textual", example: "25-Dec-2024" },
          ].map((item) => (
            <button
              key={item.format}
              onClick={() => saveDateFormat(item.format)}
              disabled={savingDateFormat}
              className={`p-4 border-2 rounded-xl text-center transition ${
                dateFormat === item.format
                  ? "border-blue-600 bg-blue-50/50"
                  : "border-slate-200 hover:border-blue-400 bg-white"
              }`}
            >
              <div className={`text-[11px] font-bold mb-1 ${dateFormat === item.format ? "text-blue-600" : "text-slate-500"}`}>{item.label}</div>
              <div className="text-[15px] font-mono text-slate-800 font-medium">{item.format}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Team Members / Assignees */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Team Members</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">People you can assign tasks to. Appears in task creation and management.</p>
        </div>

        <div className="flex gap-2 mb-5">
          <input
            type="text"
            placeholder="Full name"
            value={addAssigneeForm.name}
            onChange={(e) => setAddAssigneeForm({ ...addAssigneeForm, name: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && addAssignee()}
            className="flex-1 h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="text"
            placeholder="Role (optional)"
            value={addAssigneeForm.role}
            onChange={(e) => setAddAssigneeForm({ ...addAssigneeForm, role: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && addAssignee()}
            className="w-40 h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={addAssignee}
            disabled={addingAssignee}
            className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold inline-flex items-center gap-1.5 transition active:scale-95 disabled:opacity-60 shrink-0"
          >
            <Plus size={14} />
            Add
          </button>
        </div>

        {assignees.length === 0 ? (
          <div className="text-[13px] text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-xl">
            No team members yet. Add people above to assign tasks to them.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
            {assignees.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition">
                <div>
                  <div className="text-[13.5px] font-semibold text-slate-900">{a.name}</div>
                  {a.role && <div className="text-[11.5px] text-slate-500 mt-0.5">{a.role}</div>}
                </div>
                <button
                  onClick={() => deleteAssignee(a.id)}
                  className="h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Business Profile */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Business Profile</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Help the AI understand your business so it recognises relevant opportunities and requirements.</p>
        </div>

        <div className="space-y-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Business Name</label>
              <input
                type="text"
                value={businessProfile.businessName}
                onChange={(e) => setBusinessProfile(p => ({ ...p, businessName: e.target.value }))}
                placeholder="e.g. ABC Trading Co."
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Industry</label>
              <input
                type="text"
                value={businessProfile.businessIndustry}
                onChange={(e) => setBusinessProfile(p => ({ ...p, businessIndustry: e.target.value }))}
                placeholder="e.g. Industrial Equipment, Real Estate"
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Description of Services / Products</label>
            <textarea
              value={businessProfile.businessDescription}
              onChange={(e) => setBusinessProfile(p => ({ ...p, businessDescription: e.target.value }))}
              placeholder="e.g. We supply and rent generators, compressors, and power tools for construction and industrial use."
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Opportunity Keywords</label>
            <input
              type="text"
              value={businessProfile.opportunityKeywords}
              onChange={(e) => setBusinessProfile(p => ({ ...p, opportunityKeywords: e.target.value }))}
              placeholder="e.g. generator, compressor, equipment hire, power tools, rental"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-[11.5px] text-slate-400 mt-1.5">Comma-separated keywords. When these appear in messages, AI treats them as opportunities even in group chats.</p>
          </div>
        </div>

        <button
          onClick={saveBusinessProfile}
          disabled={savingBusinessProfile}
          className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 transition active:scale-95"
        >
          {savingBusinessProfile ? "Saving..." : "Save Business Profile"}
        </button>
      </div>

      {/* Skip Processing Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">AI Processing</h2>
          <p className="text-[13px] text-slate-600 mt-1 font-medium">Choose which chats are parsed by AI</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          {[
            ["all", "All chats", "Every chat is processed except the ones you skip."],
            ["selected", "Only selected chats", "Only chats you pick are processed. New groups are ignored until added."],
          ].map(([mode, title, desc]) => (
            <button
              key={mode}
              type="button"
              disabled={savingFilterMode}
              onClick={() => saveChatFilterMode(mode)}
              className={"text-left p-4 border rounded-xl transition disabled:opacity-60 " +
                (chatFilterMode === mode
                  ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600/20"
                  : "border-slate-200 hover:bg-slate-50")}
            >
              <div className="text-[13.5px] font-semibold text-slate-900">{title}</div>
              <p className="text-[12px] text-slate-500 mt-0.5">{desc}</p>
            </button>
          ))}
        </div>

        {onlySelected ? (
          <p className="text-[12px] text-slate-500">
            Pick the chats to process in the <span className="font-semibold text-slate-700">Selected Chats</span> list below.
            {allowedChats.length === 0 && <span className="text-amber-600 font-semibold"> No chats selected yet — nothing is being processed.</span>}
          </p>
        ) : (<>
        <div className="space-y-3 mb-5">
          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
            <div>
              <div className="text-[13.5px] font-semibold text-slate-900">Skip Group Messages</div>
              <p className="text-[12px] text-slate-500 mt-0.5">Group messages won't be parsed by AI.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={skipGroups} onChange={(e) => setSkipGroups(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
            <div>
              <div className="text-[13.5px] font-semibold text-slate-900">Skip Individual Messages</div>
              <p className="text-[12px] text-slate-500 mt-0.5">Individual messages won't be parsed by AI.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={skipIndividuals} onChange={(e) => setSkipIndividuals(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
            </label>
          </div>
        </div>

        <button
          onClick={saveUserSettings}
          disabled={savingSkipSettings}
          className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 transition active:scale-95"
        >
          {savingSkipSettings ? "Saving..." : "Save Settings"}
        </button>
        </>)}
      </div>

      {/* Chat Skip List */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{onlySelected ? "Selected Chats" : "Chat Skip List"}</h2>
            {onlySelected ? (
              <p className="text-[13px] text-slate-600 mt-1 font-medium">
                <span className="font-semibold text-slate-800">Only</span> messages from these chats are classified and turned into tasks. All other chats are ignored.
              </p>
            ) : (
              <p className="text-[13px] text-slate-600 mt-1 font-medium">
                Messages from these chats will <span className="font-semibold text-slate-800">not</span> be classified or turned into tasks — ideal for personal groups and private conversations.
              </p>
            )}
          </div>
          <button
            onClick={openSkipAdd}
            className="h-9 px-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95 shrink-0"
          >
            <Plus size={14} />
            Add Chat
          </button>
        </div>

        {listChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
            <span className="material-symbols-outlined text-slate-300 text-4xl mb-2" style={{ fontVariationSettings: "'FILL' 1" }}>{onlySelected ? "checklist" : "do_not_disturb_on"}</span>
            <div className="text-[13px] font-medium text-slate-500">{onlySelected ? "No chats selected yet" : "No chats skipped yet"}</div>
            <p className="text-[12px] text-slate-400 mt-1 max-w-xs">
              {onlySelected
                ? "No messages are being processed. Add the work groups and chats you want Brainydo to read."
                : "All incoming messages are being processed. Add personal chats here to exclude them."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {listChats.map((c) => (
              <div key={c.chat_id} className="flex items-center justify-between px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={"w-8 h-8 grid place-items-center rounded-lg " + (c.chat_type === "group" ? "bg-purple-50 border border-purple-200" : "bg-blue-50 border border-blue-200")}>
                    <span className={"material-symbols-outlined text-[17px] " + (c.chat_type === "group" ? "text-purple-600" : "text-blue-600")} style={{ fontVariationSettings: "'FILL' 1" }}>
                      {c.chat_type === "group" ? "group" : "person"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-slate-900 truncate">{c.chat_name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{c.chat_type} · {c.chat_id}</div>
                  </div>
                </div>
                <button
                  onClick={() => removeFromSkipList(c.chat_id)}
                  className="ml-3 h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition shrink-0"
                  title={`Remove from ${listLabel}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add to Skip List Modal */}
        {showSkipAdd && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
                <h3 className="text-[16px] font-bold text-slate-900">{onlySelected ? "Add Chat to Process" : "Add Chat to Skip List"}</h3>
                <button onClick={() => setShowSkipAdd(false)} className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-lg transition text-slate-500 text-lg leading-none">
                  &times;
                </button>
              </div>

              {/* Tab switcher */}
              <div className="px-6 pt-4 flex gap-1 bg-slate-50 border-b border-slate-200 shrink-0">
                {[["groups", "Groups"], ["individuals", "Individuals"], ["manual", "Manual Entry"]].map(([tab, label]) => (
                  <button
                    key={tab}
                    onClick={() => { setSkipAddTab(tab); setAvailableChatsSearch(""); }}
                    className={"px-4 py-2 text-[13px] font-semibold rounded-t-lg border border-b-0 transition " +
                      (skipAddTab === tab
                        ? "bg-white border-slate-200 text-slate-900 -mb-px"
                        : "border-transparent text-slate-500 hover:text-slate-700")}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1">
                {(skipAddTab === "groups" || skipAddTab === "individuals") ? (
                  <AvailableChatsList
                    chats={availableChats}
                    loading={availableChatsLoading}
                    type={skipAddTab === "groups" ? "group" : "individual"}
                    search={availableChatsSearch}
                    onSearch={setAvailableChatsSearch}
                    skippedChats={listChats}
                    addedLabel={onlySelected ? "Added" : "Skipped"}
                    onAdd={async (c) => {
                      await addToSkipList(c);
                      setShowSkipAdd(false);
                    }}
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-[12px] text-slate-500">Manually enter the chat details. The Chat ID is the WhatsApp chat identifier (e.g. <span className="font-mono text-slate-700">123456789@g.us</span> for groups).</p>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Chat Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Family Group, John Personal"
                        value={skipManualForm.chat_name}
                        onChange={(e) => setSkipManualForm({ ...skipManualForm, chat_name: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Chat ID</label>
                      <input
                        type="text"
                        placeholder="e.g. 917012345678@s.whatsapp.net"
                        value={skipManualForm.chat_id}
                        onChange={(e) => setSkipManualForm({ ...skipManualForm, chat_id: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Type</label>
                      <div className="flex gap-2">
                        {["individual", "group"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setSkipManualForm({ ...skipManualForm, chat_type: t })}
                            className={"flex-1 h-10 text-[13px] font-semibold rounded-xl border transition " +
                              (skipManualForm.chat_type === t
                                ? "bg-blue-600 text-white border-blue-600"
                                : "border-slate-200 text-slate-600 hover:border-slate-900")}
                          >
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      <button
                        onClick={addManualSkip}
                        className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold transition active:scale-95"
                      >
                        {onlySelected ? "Add to Selected Chats" : "Add to Skip List"}
                      </button>
                      <button onClick={() => setShowSkipAdd(false)} className="flex-1 h-10 border border-slate-200 hover:border-slate-900 rounded-xl text-[14px] transition">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Message Classifiers */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Custom Classifiers</h2>
            <p className="text-[13px] text-slate-600 mt-1 font-medium">Add custom message categories for your workflow</p>
          </div>
          {!showAddClassifier && (
            <button
              onClick={() => setShowAddClassifier(true)}
              className="h-9 px-3 inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg text-[13px] hover:bg-blue-700 transition"
            >
              <Plus size={14} />
              Add
            </button>
          )}
        </div>

        {showAddClassifier && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4">
            <input
              type="text"
              placeholder="Classifier name (e.g., 'Complaint', 'Feedback')"
              value={newClassifier.name}
              onChange={(e) => setNewClassifier({ ...newClassifier, name: e.target.value })}
              className="w-full h-9 px-3 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3"
            />
            <textarea
              placeholder="Description (optional)"
              value={newClassifier.description}
              onChange={(e) => setNewClassifier({ ...newClassifier, description: e.target.value })}
              rows={2}
              className="w-full p-2 border border-slate-200 rounded-sm text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddClassifier}
                disabled={classifierLoading}
                className="h-8 px-3 bg-blue-600 text-white rounded-lg text-[13px] hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {classifierLoading ? "Adding..." : "Add"}
              </button>
              <button
                onClick={() => setShowAddClassifier(false)}
                className="h-8 px-3 border border-slate-200 rounded-sm text-[13px] hover:bg-slate-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {classifiers.length > 0 ? (
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="divide-y divide-slate-200">
              {classifiers.map((classifier) => (
                <div key={classifier.id} className="p-4 hover:bg-slate-50 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-[13px] font-medium text-slate-900">{classifier.name}</div>
                    {classifier.description && (
                      <p className="text-[12px] text-slate-500 mt-1">{classifier.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteClassifier(classifier.id)}
                    className="ml-4 px-2 py-1 border border-red-200 text-red-600 rounded-sm text-[11px] hover:bg-red-50"
                  >
                    <Trash2 size={12} className="inline" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-500 text-[13px] py-6">No custom classifiers yet</p>
        )}
      </div>

      {/* Default Message Classifier */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Built-in Categories</h2>
        <p className="text-[12px] text-slate-500 mb-4">
          Standard message categories available in BrainyDo:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="text-[12px] space-y-1">
            {CATEGORIES.slice(0, 5).map((cat) => (
              <div key={cat.key}><span className="font-medium">{cat.label}</span></div>
            ))}
          </div>
          <div className="text-[12px] space-y-1">
            {CATEGORIES.slice(5).map((cat) => (
              <div key={cat.key}><span className="font-medium">{cat.label}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Connection Schedule */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-bold text-slate-900 uppercase tracking-wider">Connection Schedule</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">Off = Brainydo stays connected 24/7 (recommended). On = connected only during these hours; messages that arrive outside are read when the window opens, so tasks from them appear later.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} />
            <div className="w-10 h-5 bg-slate-200 peer-checked:bg-blue-600 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
          </label>
        </div>
        {scheduleEnabled && (
          <div className="px-6 py-5 space-y-5">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Connect from</label>
                <input
                  type="time"
                  value={scheduleFrom}
                  onChange={(e) => setScheduleFrom(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-slate-900 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Disconnect at</label>
                <input
                  type="time"
                  value={scheduleTo}
                  onChange={(e) => setScheduleTo(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-slate-900 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Active days</label>
              <div className="flex flex-wrap gap-2">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setScheduleDays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort())}
                    className={`w-11 h-9 rounded-lg text-[12px] font-semibold border transition ${
                      scheduleDays.includes(i)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Timezone: Asia/Kolkata (IST). The system will auto-connect at your start time and auto-disconnect at end time daily.
              </p>
              <p className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                Messages received while disconnected are not lost — WhatsApp queues them and delivers them to Brainydo automatically when the connection resumes at your scheduled start time.
              </p>
            </div>
            <button
              onClick={saveSchedule}
              disabled={savingSchedule}
              className="h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition disabled:opacity-60"
            >
              {savingSchedule ? "Saving…" : "Save Schedule"}
            </button>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-white border border-red-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={15} className="text-red-500" />
          <h2 className="text-[13px] font-bold text-red-600 uppercase tracking-wider">Danger Zone</h2>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[12.5px] text-slate-500 max-w-xl">
            Permanently delete this account: all messages, tasks, leads, team members and settings are
            erased and cannot be recovered. Only the account owner can do this.
          </p>
          <button
            onClick={() => setShowDeleteAccount(true)}
            className="h-9 px-4 inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 rounded-xl text-[12.5px] font-semibold transition"
          >
            <Trash2 size={13} /> Delete account
          </button>
        </div>
      </div>

      {showDeleteAccount && <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />}

      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900">Choose a Plan</h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition text-slate-500"
              >
                &#x2715;
              </button>
            </div>
            <div className="p-6">
              {upgradePlansLoading && (
                <div className="text-center py-8 text-slate-500 text-sm">Loading plans&#8230;</div>
              )}
              {!upgradePlansLoading && upgradePlans.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">No plans available.</div>
              )}
              {!upgradePlansLoading && upgradePlans.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {upgradePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="border border-slate-200 rounded-xl p-5 hover:border-blue-400 transition cursor-pointer"
                      onClick={() => {
                        setShowUpgradeModal(false);
                        openCheckout(plan);
                      }}
                    >
                      <div className="font-semibold text-slate-900 mb-1">{plan.name}</div>
                      {plan.description && (
                        <div className="text-xs text-slate-500 mb-3">{plan.description}</div>
                      )}
                      <div className="text-2xl font-bold text-slate-900">
                        {plan.currency === "INR" ? "₹" : plan.currency}{plan.price.toLocaleString()}
                        <span className="text-sm font-normal text-slate-500 ml-1">/ {plan.duration_days} days</span>
                      </div>
                      {plan.features && plan.features.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {plan.features.slice(0, 3).map((f, fi) => (
                            <li key={fi} className="text-xs text-slate-600 flex items-start gap-1.5">
                              <span className="text-blue-500 shrink-0">&#x2713;</span>{f}
                            </li>
                          ))}
                          {plan.features.length > 3 && (
                            <li className="text-xs text-slate-400">+{plan.features.length - 3} more</li>
                          )}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteAccountModal({ onClose }) {
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const canDelete = password && confirmText === "DELETE" && !busy;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password });
      localStorage.removeItem("sw_token");
      localStorage.removeItem("sw_user");
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete account");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="w-11 h-11 rounded-full bg-red-50 grid place-items-center mb-4">
          <AlertTriangle size={20} className="text-red-600" />
        </div>
        <h2 className="text-[17px] font-bold text-slate-900 mb-1.5">Delete this account permanently?</h2>
        <p className="text-[13px] text-slate-500 mb-5 leading-relaxed">
          Every message, task, lead, setting and team membership will be erased and your WhatsApp
          connection removed. <strong>This cannot be undone.</strong>
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
              Your password
            </label>
            <input
              type="password" autoFocus autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
              Type DELETE to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] font-mono focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 h-10 border border-slate-200 hover:border-slate-400 rounded-xl text-[13px] font-semibold text-slate-700 transition"
            >
              Cancel
            </button>
            <button
              onClick={doDelete}
              disabled={!canDelete}
              className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[13px] font-semibold transition disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {busy ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AvailableChatsList({ chats, loading, type, search, onSearch, skippedChats, addedLabel = "Skipped", onAdd }) {
  const isGroup = type === "group";

  const getDisplayName = (c) => {
    if (isGroup) return c.chat_name || c.chat_id;
    // For individuals, sender_name is the WhatsApp display name; chat_name is usually the raw phone number
    return c.sender_name || c.chat_name || c.chat_id;
  };

  const getPhoneFromChatId = (chat_id) => {
    // Extract the phone/number part before @ if present
    return chat_id ? chat_id.split("@")[0] : "";
  };

  const filtered = chats
    .filter((c) => c.chat_type === type)
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const displayName = getDisplayName(c).toLowerCase();
      const phone = getPhoneFromChatId(c.chat_id);
      return displayName.includes(q) || phone.includes(q) || (c.chat_id || "").toLowerCase().includes(q);
    });

  return (
    <div>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={isGroup ? "Search groups…" : "Search by name or phone number…"}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-xl text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
          autoFocus
        />
      </div>

      {loading ? (
        <div className="text-center py-8 text-[13px] text-slate-500">Loading chats…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-[13px] text-slate-500">
          {chats.filter((c) => c.chat_type === type).length === 0
            ? `No ${isGroup ? "group" : "individual"} chats found in message history.`
            : "No results match your search."}
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-0.5">
          {filtered.map((c) => {
            const alreadySkipped = skippedChats.some((s) => s.chat_id === c.chat_id);
            const displayName = getDisplayName(c);
            const phone = getPhoneFromChatId(c.chat_id);
            return (
              <button
                key={c.chat_id}
                disabled={alreadySkipped}
                onClick={() => onAdd({ chat_id: c.chat_id, chat_name: displayName, chat_type: c.chat_type })}
                className={"w-full flex items-center gap-3 px-4 py-3 border rounded-xl text-left transition " +
                  (alreadySkipped
                    ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                    : "border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 active:scale-[0.99]")}
              >
                <div className={"w-8 h-8 grid place-items-center rounded-lg shrink-0 " + (isGroup ? "bg-purple-50 border border-purple-200" : "bg-blue-50 border border-blue-200")}>
                  <span className={"material-symbols-outlined text-[17px] " + (isGroup ? "text-purple-600" : "text-blue-600")} style={{ fontVariationSettings: "'FILL' 1" }}>
                    {isGroup ? "group" : "person"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{displayName}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {!isGroup && phone && <span className="mr-2">+{phone}</span>}
                    {c.message_count ? `${c.message_count} msg${c.message_count !== 1 ? "s" : ""}` : "no messages yet"}
                  </div>
                </div>
                {alreadySkipped
                  ? <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide shrink-0">{addedLabel}</span>
                  : <span className="material-symbols-outlined text-[16px] text-slate-300 shrink-0">add_circle</span>
                }
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
